import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Users, Phone, FileSpreadsheet, Loader2, FileText } from 'lucide-react';
import { adminAPI } from '@/api/client';
import { toast } from 'sonner';

const ExportNumbers = () => {
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingTxt, setLoadingTxt] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const handlePreview = async () => {
    if (!category) {
      toast.error('Please select a category (PB or CD)');
      return;
    }

    setLoading(true);
    try {
      const response = await adminAPI.getStudentsByCategory(category);
      setPreviewData(response.data);
      toast.success(`Found ${response.data.total} students`);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!category) {
      toast.error('Please select a category (PB or CD)');
      return;
    }

    setLoading(true);
    try {
      const response = await adminAPI.exportStudentNumbers(category);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${category}_students_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export CSV');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPhonesTxt = async () => {
    if (!category) {
      toast.error('Please select a category (PB or CD)');
      return;
    }

    setLoadingTxt(true);
    try {
      const response = await adminAPI.exportStudentPhonesTxt(category);
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/plain;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${category}_phone_numbers_${new Date().toISOString().split('T')[0]}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Phone numbers TXT exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export phone numbers');
    } finally {
      setLoadingTxt(false);
    }
  };

  const handleExportAllPhonesTxt = async () => {
    setLoadingTxt(true);
    try {
      const response = await adminAPI.exportStudentPhonesTxt('ALL');
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/plain;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `all_phone_numbers_${new Date().toISOString().split('T')[0]}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('All phone numbers TXT exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export phone numbers');
    } finally {
      setLoadingTxt(false);
    }
  };

  const handleExportAll = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.exportStudentNumbers('ALL');
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `all_students_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('All students CSV exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export CSV');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Export Student Numbers</h2>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Export contact details for campaigns</p>
        </div>
      </div>

      {/* Export Options Card */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <FileSpreadsheet className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
            Export by Category
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Select exam category to export student names and phone numbers
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 p-4 sm:p-6 pt-0 sm:pt-0">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="w-full sm:w-48">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Exam Category
              </label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-full text-sm">
                  <SelectValue placeholder="Select Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PB">
                    <span className="flex items-center gap-2">
                      📝 PB (Paper Based)
                    </span>
                  </SelectItem>
                  <SelectItem value="CD">
                    <span className="flex items-center gap-2">
                      💻 CD (Computer Delivered)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-wrap items-end gap-2">
              <Button 
                variant="outline" 
                onClick={handlePreview}
                disabled={loading || loadingTxt || !category}
                size="sm"
                className="text-xs sm:text-sm"
              >
                {loading ? <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin mr-1 sm:mr-2" /> : <Users className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />}
                Preview
              </Button>
              <Button 
                onClick={handleExport}
                disabled={loading || loadingTxt || !category}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm"
              >
                {loading ? <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin mr-1 sm:mr-2" /> : <FileSpreadsheet className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />}
                Export CSV
              </Button>
              <Button 
                onClick={handleExportPhonesTxt}
                disabled={loading || loadingTxt || !category}
                size="sm"
                variant="outline"
                className="border-blue-500 text-blue-600 hover:bg-blue-50 text-xs sm:text-sm"
              >
                {loadingTxt ? <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin mr-1 sm:mr-2" /> : <Phone className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />}
                Export Numbers (TXT)
              </Button>
            </div>
          </div>

          {/* Preview Data */}
          {previewData && (
            <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-2 sm:mb-3">
                <h4 className="font-semibold text-sm sm:text-base text-gray-700">
                  Preview: {previewData.total} {category} Students
                </h4>
                <span className="text-xs sm:text-sm text-gray-500">
                  Showing first 5 records
                </span>
              </div>
              <div className="overflow-x-auto -mx-3 sm:mx-0">
                <table className="min-w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-1.5 sm:py-2 px-2 sm:px-3 font-medium text-gray-600">Name</th>
                      <th className="text-left py-1.5 sm:py-2 px-2 sm:px-3 font-medium text-gray-600">Phone</th>
                      <th className="text-left py-1.5 sm:py-2 px-2 sm:px-3 font-medium text-gray-600 hidden sm:table-cell">Email</th>
                      <th className="text-left py-1.5 sm:py-2 px-2 sm:px-3 font-medium text-gray-600 hidden md:table-cell">Batch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.preview?.map((student, index) => (
                      <tr key={index} className="border-b border-gray-100">
                        <td className="py-1.5 sm:py-2 px-2 sm:px-3">{student.name}</td>
                        <td className="py-1.5 sm:py-2 px-2 sm:px-3">
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3 text-gray-400" />
                            {student.phone || 'N/A'}
                          </span>
                        </td>
                        <td className="py-1.5 sm:py-2 px-2 sm:px-3 text-gray-500 hidden sm:table-cell">{student.email}</td>
                        <td className="py-1.5 sm:py-2 px-2 sm:px-3 hidden md:table-cell">{student.batch_code || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Export All Card */}
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Users className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            Export All Students
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Export all students regardless of category
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={handleExportAll}
              disabled={loading || loadingTxt}
              variant="outline"
              size="sm"
              className="text-xs sm:text-sm"
            >
              {loading ? <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin mr-1 sm:mr-2" /> : <FileSpreadsheet className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />}
              Export All CSV
            </Button>
            <Button 
              onClick={handleExportAllPhonesTxt}
              disabled={loading || loadingTxt}
              variant="outline"
              size="sm"
              className="border-blue-500 text-blue-600 hover:bg-blue-50 text-xs sm:text-sm"
            >
              {loadingTxt ? <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin mr-1 sm:mr-2" /> : <Phone className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />}
              Export All Numbers (TXT)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 sm:pt-6">
          <div className="flex gap-2 sm:gap-3">
            <div className="text-blue-600 mt-0.5 text-sm">ℹ️</div>
            <div className="text-xs sm:text-sm text-blue-800">
              <p className="font-medium mb-1">Export Formats:</p>
              <div className="space-y-2">
                <div>
                  <span className="font-medium">CSV File:</span>
                  <ul className="list-disc list-inside space-y-0.5 text-blue-700 text-xs sm:text-sm ml-2">
                    <li>Student Name, Phone, Email, Batch, Category</li>
                  </ul>
                </div>
                <div>
                  <span className="font-medium">TXT File (Numbers Only):</span>
                  <ul className="list-disc list-inside space-y-0.5 text-blue-700 text-xs sm:text-sm ml-2">
                    <li>Format: 01712773829,01234567897,01555123456</li>
                    <li>Comma-separated, no spaces</li>
                    <li>Ready for bulk SMS/WhatsApp campaigns</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExportNumbers;
